<script setup>
import dayjs from 'dayjs'
import { ref, computed, watch } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxColorBox } from 'devextreme-vue/color-box'
import { useDataGridWidget, useProjectPresentationExport } from '@/composables/data-grid-toolbar'
import { useGridRef, useODataSource, useLookup, useDefaultEditing, useDefaultGridOptions, useDefaultExport, useCurrencyColumnFormatter, useUpdateMasterDetail, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import { useArchivedAtColumn, useArchivedColumn, useListingColumn, useStartColumn, useEndColumn, useBudgetDocumentColumn, useProjectColumn } from '@/composables/data-grid-column'
import { useArchive } from '@/composables/archive'
import { formatMinutes } from '@/composables/cost-report'
import { useChips } from '@/composables/chips'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useAccountingStore } from '@/stores/accounting.store'

import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import ArchiveGridButton from '@/components/shared/ArchiveGridButton.vue'
import ArchivedGridToggle from '@/components/shared/ArchivedGridToggle.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ProjectEdit from '@/components/shared/ProjectEdit.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'

import CostReportGridDetail from '@/components/accounting/CostReportGridDetail.vue'
import InvoiceEdit from '@/components/accounting/InvoiceEdit.vue'
import ReportEdit from '@/components/accounting/ReportEdit.vue'
import ReportFileDialog from '@/components/accounting/ReportFileDialog.vue'
import { rangeForPreset } from '@/composables/date-range-presets'
import { usePortalStore } from '@/stores/portal.store'

const gridId = 'cost-reports-grid'

const i18n = getI18n()
const dataGridWidget = useDataGridWidget()
const createReportButton = ref(null)
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const editItem = ref(null)
const pendingUrls = ref({})
const localizedTags = planningStore.getLocalizedProjectTags()
const selectedRows = ref([])

const { gridRef } = useGridRef()
const { getProjectTagChips } = useChips()
const { exportOptions, onExporting } = useDefaultExport({ fileName: i18n.t('common.costReport', 2) })
const { archiveWarning, showArchived, archiveButton, archiveAsync, archiveDisplayExpr, archiveCellTemplate, onRowPrepared, onRowRemoving } = useArchive({
  onArchive: async(id) => { await planningStore.archiveProjectAsync(id) },
  onDelete: async(id, config) => { await planningStore.deleteProjectAsync(id, config) },
  gridRef: gridRef,
})
const editors = {}

const gridDataSource = useODataSource(planningApi.getAbsoluteUrl() + planningApi.projectsUrl, {
  customParamsFn: () => { return showArchived.value ? { show_archived: true } : {} }
})

planningStore.fetchProjectTagsAsync({ all: true }, true)
accountingStore.fetchBudgetDocumentExportTemplatesAsync({}, true)
accountingStore.fetchCompanyDataAsync({}, true)


const onContentReady = () => {
  if (webSiteStore.editedProjectId) {
    gridRef.value.instance.columnOption('id', 'filterValue', webSiteStore.editedProjectId)
    webSiteStore.editedProjectId = null
  }
}

const columns = [
  {
    type: 'buttons',
    buttons: [
      { name: 'edit',
        visible: (e) => !e.row?.data?.archived,
        onClick: (e) => {
          editItem.value = { ...e.row.data }
        },
      },
      { name: 'delete', visible: (e) => !e.row?.data?.archived },
      archiveButton,
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '6rem',
  },
  {
    dataField: 'id',
    caption: i18n.t('common.id'),
    width: '3rem',
  },
  useStartColumn({ sortOrder: 'desc', validationRules: [{ type: 'required' }]}),
  useProjectColumn({
    dataField: 'name',
    projectOf: ({ data }) => data,
    lookup: undefined,
    sortOrder: 'desc',
    validationRules: [{ type: 'required' }],
    width: '12rem',
  }),
  useListingColumn({archiveDisplayExpr, archiveCellTemplate }),
  {
    dataField: 'status', caption: i18n.t('common.status'),
    lookup: useLookup({store: planningStore.getLocalizedProjectStatuses(), displayExpr: 'name' }),
    cellTemplate: 'status-cell-template',
  },
   {
    dataField: 'total_worked_minutes',
    caption: i18n.t('common.totalWorkedHours'),
    dataType: 'number',
    calculateDisplayValue: (row) => formatMinutes(row.total_worked_minutes),
    allowEditing: false,
  },
  {
    dataField: 'total_reported_minutes',
    caption: i18n.t('common.totalReportedHours'),
    dataType: 'number',
    calculateDisplayValue: (row) => formatMinutes(row.total_reported_minutes),
    allowEditing: false,
  },
  {
    dataField: 'total_travel_minutes',
    caption: i18n.t('common.totalTravelMinutes'),
    dataType: 'number',
    calculateDisplayValue: (row) => formatMinutes(row.total_travel_minutes),
    allowEditing: false,
    visible: false,
  },
  {
    dataField: 'total_pause_minutes',
    caption: i18n.t('common.totalPauseMinutes'),
    dataType: 'number',
    calculateDisplayValue: (row) => formatMinutes(row.total_pause_minutes),
    allowEditing: false,
    visible: false,
  },
  {
    dataField: 'total_cost',
    caption: i18n.t('common.totalCost'),
    dataType: 'number',
    allowEditing: false,
    calculateDisplayValue: (rowData) => rowData.total_cost != null ? useCurrencyColumnFormatter(rowData, 'total_cost', rowData.currency_ref) : '',
  },
  useBudgetDocumentColumn({
    visible: webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS),
    showInColumnChooser: webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS),
  }),
  {
    dataField: 'color', caption: i18n.t('common.color'),
    editCellTemplate: 'colorPicker',
    showInColumnChooser: false,
    setCellValue: (newData, value) => {
      newData.color = value
    },
    visible: false,
  },
  useEndColumn({ validationRules: [{ type: 'required' }]}),
  { dataField: 'description', caption: i18n.t('common.description'), width: '12rem' },
  {
    dataField: 'currency_ref',
    caption: i18n.t('common.currency'),
    lookup: useLookup({ dataSource: webSiteStore.getLocalizedCurrencies(), valueExpr: 'id', displayExpr: 'name' }),
  },
  {
    dataField: 'tag_ids',
    caption: i18n.t('common.tag', 2),
    width: '12rem',
    cellTemplate: 'project-tags-cell-template',
    lookup: useLookup({
        dataSource: localizedTags,
        valueExpr: 'id',
        displayExpr: 'name',
      }),
  },
  {
    dataField: 'project_files',
    caption: i18n.t('common.attachment', 2),
    cellTemplate:'fileViewer',
    editCellTemplate: 'fileUploader',
    width: '16rem',
    allowExporting: false,
  },
  useArchivedAtColumn(),
  useArchivedColumn(),
]

const defaultEnd = (value) => {
  return dayjs(`${dayjs(value).format('YYYY-MM-DD')}T${webSiteStore.getDefaultEndTime()}`).toISOString()
}

const onInitNewRow = (e) => {
  e.data.status = 0
  e.data.start = dayjs(`${dayjs().format('YYYY-MM-DD')}T${webSiteStore.getDefaultStartTime()}`).toISOString()
  e.data.end = defaultEnd(e.data.start)
}

const alwaysEditableColumns = ['name', 'description', 'status', 'tags', 'project_files']

const onEditorPreparing = async(e) => {
  if (e.parentType === 'dataRow') {
    const fieldName = e.dataField
    e.editorOptions.onInitialized = (e) => { editors[fieldName] = e.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'start') {
    const defaultOnValueChanged = e.editorOptions.onValueChanged
    e.editorOptions.onValueChanged = (e) => {
      defaultOnValueChanged(e)
      if (editors['end']) {
        editors['end'].option('min', e.value?.toISOString())
        editors['end'].option('value', defaultEnd(e.value))
      }
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'end') {
    e.editorOptions.onInitialized = (args) => {
      editors[e.dataField] = args.component
      if (e.row.data?.start) {
        args.component.option('min', e.row.data.start)
      }
    }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'name' && e.row?.data?.id) {
    e.promise = planningStore.fetchProjectByIdAsync(e.row.data?.id).then((project) => {
      for (const editor in editors) {
        if (project.data?.has_listing_items && !alwaysEditableColumns.includes(editor)) {
          editors[editor].option('readOnly', true)
        }
      }
    })
  }
}

const onRowUpdating = async(e) => {
  if (!e.newData.project_files) {
    e.newData.project_files = e.oldData.project_files || []
  }
  if (pendingUrls.value[e.key] !== undefined) {
    e.newData.project_urls = pendingUrls.value[e.key]
    delete pendingUrls.value[e.key]
  } else {
    e.newData.project_urls = e.oldData.project_urls || []
  }
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  createReportButton.value?.option('disabled', e.selectedRowsData.length === 0)
  dataGridWidget.printWidgetButton.value?.option('disabled', !canPrintSelection(e.selectedRowsData))
}

const reportEditItem = ref(null)
const reportPrefill = ref({})
const generatedReport = ref(null)

const selectedCompanies = () => [...new Set(selectedRows.value.map(project => project.company))]

const selectedSpan = () => {
  const starts = selectedRows.value.map(project => project.start).filter(Boolean).map(value => dayjs(value))
  const ends = selectedRows.value.map(project => project.end).filter(Boolean).map(value => dayjs(value))
  return {
    spanFrom: starts.length ? starts.reduce((earliest, value) => (value.isBefore(earliest) ? value : earliest)) : null,
    spanTo: ends.length ? ends.reduce((latest, value) => (value.isAfter(latest) ? value : latest)) : null,
  }
}

const canPrintSelection = (rows) => rows.length > 0 && rows.every(project => project.start && project.end)

const printParams = ref(null)
const openPrint = () => {
  const { spanFrom, spanTo } = selectedSpan()
  const companies = selectedCompanies()
  printParams.value = {
    subject: accountingStore.reportSubjectEnum.COMPANY,
    company: companies.length === 1 ? companies[0] : null,
    project_ids: selectedRows.value.map(project => project.id),
    date_from: spanFrom.format('YYYY-MM-DD'),
    date_to: spanTo.format('YYYY-MM-DD'),
  }
}

const openReportWizard = () => {
  const companies = selectedCompanies()
  const month = rangeForPreset(portalStore.dateRangePresetEnum.MONTH)
  const { spanFrom, spanTo } = selectedSpan()
  const insideMonth = !spanFrom || !spanTo || (!spanFrom.isBefore(month.from, 'day') && !spanTo.isAfter(month.to, 'day'))
  reportPrefill.value = {
    ...(companies.length === 1 && companies[0] ? { subject: accountingStore.reportSubjectEnum.COMPANY, company: companies[0] } : {}),
    ...(insideMonth ? {} : { period_preset: null, date_from: spanFrom.format('YYYY-MM-DD'), date_to: spanTo.format('YYYY-MM-DD') }),
  }
  reportEditItem.value = {}
}

const onReportGenerated = (report) => {
  reportEditItem.value = null
  generatedReport.value = report
}


const invoiceProjectIds = computed(() => [...new Set([
  ...selectedRows.value.map(project => project.id),
  ...accountingStore.invoiceSelectionRows().map(row => row.project),
])])
const hasInvoiceSelection = computed(() => invoiceProjectIds.value.length > 0)
watch(hasInvoiceSelection, (value) => dataGridWidget.createInvoiceWidgetButton.value?.option('disabled', !value))

const gridOptions = useDefaultGridOptions({
  id: gridId,
  dateSerializationFormat: 'yyyy-MM-ddTHH:mm:ssZ',
  dataSource: gridDataSource,
  columns: columns,
  editing: useDefaultEditing(),
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  masterDetail: {
    enabled: true,
    template: 'master-detail',
  },
  allowMultipleMasterDetail: true,
  export: exportOptions,
  onInitNewRow: onInitNewRow,
  onEditorPreparing: onEditorPreparing,
  onRowPrepared: onRowPrepared,
  presentationExports: [useProjectPresentationExport(i18n.t('common.costReport'))],
  exportWidgets: [
    dataGridWidget.createInvoiceWidget({ onClick: () => { showInvoiceWizard.value = true } }),
    dataGridWidget.actionWidget(createReportButton, { icon: 'mdi mdi-file-chart-outline', text: i18n.t('common.report.generate'), onClick: openReportWizard }),
    ...(webSiteStore.can(webSiteStore.permissionFlagEnum.REPORTS) ? [dataGridWidget.printWidget({ onClick: openPrint })] : []),
  ],
  onRowRemoving: onRowRemoving,
  onContentReady: onContentReady,
  onRowUpdating: onRowUpdating,
  onExporting: onExporting,
  onSelectionChanged: onSelectionChanged,
  clearFilterButton: true,
  archivedToggle: true,
  addButton: { onClick: openReportWizard },
})

const onDownloadProjectFileAsync = async(e) => {
  if (e) {
    await planningStore.downloadProjectFileAsync(e.id, e.name)
  }
}

const onColorChanged = (e, templateOptions) => {
  templateOptions.setValue(e.value)
}

const updateGrid = async (saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await planningStore.fetchProjectTagsAsync({ all: true }, true)
  await refreshGridAsync(gridRef, createdId)
}

const { repaintRow } = useUpdateMasterDetail()
const onDetailRowSaved = async (parentId) => {
  await repaintRow(gridRef, planningApi.getProjectByIdAsync, parentId, parentId)
}

const showInvoiceWizard = ref(false)


const onInvoiceCreated = () => {
  showInvoiceWizard.value = false
  accountingStore.clearInvoiceSelection()
}

const onInvoiceWizardCancel = () => { showInvoiceWizard.value = false }

</script>
<template>
  <div>
    <q-dialog :model-value="editItem !== null" class="dialog-sm" @hide="updateGrid">
      <project-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="updateGrid"
        @cancel="updateGrid"
        @delete="updateGrid"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <q-dialog v-model="showInvoiceWizard" class="dialog-xl" persistent>
      <invoice-edit
        v-if="showInvoiceWizard"
        :project-ids="invoiceProjectIds"
        :selected-cost-report-row-ids="accountingStore.invoiceSelectionRows().map(row => row.id)"
        @update="onInvoiceCreated"
        @cancel="onInvoiceWizardCancel"
      />
    </q-dialog>
    <q-dialog :model-value="reportEditItem !== null" class="dialog-lg" persistent>
      <report-edit
        v-if="reportEditItem !== null"
        :model-value="reportEditItem"
        :prefill="reportPrefill"
        @update="onReportGenerated"
        @cancel="reportEditItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="printParams !== null" @hide="printParams = null">
      <report-file-dialog v-if="printParams !== null" :params="printParams" @cancel="printParams = null" />
    </q-dialog>
    <q-dialog :model-value="generatedReport !== null" @hide="generatedReport = null">
      <report-file-dialog v-if="generatedReport !== null" :report="generatedReport" @cancel="generatedReport = null" />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #master-detail="{ data: templateData }">
        <cost-report-grid-detail
          :project="templateData.data"
          @row-saved="onDetailRowSaved"
        />
      </template>
      <template #project-tags-cell-template="{ data: templateOptions }">
        <chip-template :values="getProjectTagChips(templateOptions.data.tag_ids)" />
      </template>
      <template #status-cell-template="{ data: templateOptions }">
        <chip-template :values="[planningStore.getLocalizedProjectStatuses()[templateOptions.data.status]]" />
      </template>
      <template #fileUploader="{ data: templateOptions }">
        <file-url-edit
          :cell-info="templateOptions"
          :urls="templateOptions.data.project_urls"
          @download="onDownloadProjectFileAsync"
          @update:urls="pendingUrls[templateOptions.key] = $event"
        />
      </template>
      <template #fileViewer="{ data: templateOptions }">
        <file-viewer
          dense
          :cell-info="templateOptions"
          :urls="templateOptions.data.project_urls"
        />
      </template>
      <template #colorPicker="{ data: templateOptions }">
        <dx-color-box
          :value="templateOptions.data.color"
          @value-changed="(e) => onColorChanged(e, templateOptions)"
        />
      </template>
      <template #archive-button="{ data: row }">
        <archive-grid-button :row="row" />
      </template>
      <template #archived-toggle>
        <archived-grid-toggle
          :show-archived="showArchived"
          :grid-ref="gridRef"
          @update:show-archived="showArchived = $event"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
